import React, { useState, useEffect, useMemo } from "react";
import { 
    Card, Table, Row, Col, Form, InputGroup, Button, Badge, 
    Dropdown, Spinner, Alert, Tooltip, OverlayTrigger, Modal, Pagination 
} from "react-bootstrap";
import { Link, useNavigate } from "react-router-dom";
import Footer from "../layouts/Footer";
import Header from "../layouts/Header";
import { formatCurrency, formatDate } from "../utils/formatters";
import './Factures.css';

export default function BonsLivraison() {
    const navigate = useNavigate();
    const [bonsLivraison, setBonsLivraison] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [filterText, setFilterText] = useState('');
    const [stats, setStats] = useState(null);
    const [filtreStatut, setFiltreStatut] = useState('tous');
    const [showFilters, setShowFilters] = useState(false);
    const [sortConfig, setSortConfig] = useState({ key: 'date_livraison', direction: 'desc' });
    const [societeInfos, setSocieteInfos] = useState(null);
    const [taxes, setTaxes] = useState({
        tva_taux: 18,
        css_taux: 1,
        tva_active: true,
        css_active: true
    });

    // États pour la pagination
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(10);

    const [showSuccessAlert, setShowSuccessAlert] = useState(false);
    const [showErrorAlert, setShowErrorAlert] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');
    const [errorMessage, setErrorMessage] = useState('');

    // États pour le modal de confirmation
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [confirmAction, setConfirmAction] = useState(null);
    const [confirmId, setConfirmId] = useState(null);
    const [confirmTitle, setConfirmTitle] = useState('');
    const [confirmMessage, setConfirmMessage] = useState('');
    const [confirmVariant, setConfirmVariant] = useState('primary');
    const [isProcessing, setIsProcessing] = useState(false);

    useEffect(() => {
        fetchBonsLivraison();
        fetchStats();
        fetchSocieteInfos();
        fetchTaxes();
    }, [filtreStatut]);

    const fetchSocieteInfos = async () => {
        try {
            const token = localStorage.getItem("token");
            const response = await fetch(
                'https://sawd.julesferry-ga.com/api/parametres.php?action=societe',
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            
            if (response.ok) {
                const result = await response.json();
                if (result.data) {
                    setSocieteInfos(result.data);
                }
            }
        } catch (err) {
            console.error("Erreur chargement société", err);
        }
    };

    const fetchTaxes = async () => {
        try {
            const token = localStorage.getItem("token");
            const response = await fetch(
                'https://sawd.julesferry-ga.com/api/parametres.php?action=taxes',
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            if (response.ok) {
                const result = await response.json();
                if (result.data) {
                    setTaxes(result.data);
                }
            }
        } catch (err) {
            console.error("Erreur chargement taxes", err);
        }
    };

    const openConfirmModal = (id, title, message, variant = 'primary', action) => {
        setConfirmId(id);
        setConfirmTitle(title);
        setConfirmMessage(message);
        setConfirmVariant(variant);
        setConfirmAction(() => action);
        setShowConfirmModal(true);
    };

    const fetchBonsLivraison = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem("token");
            let url = 'https://sawd.julesferry-ga.com/api/bons_livraison.php';
            
            if (filtreStatut !== 'tous') {
                url += `?statut=${filtreStatut}`;
            }
            
            const response = await fetch(url, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            
            if (!response.ok) throw new Error("Erreur chargement");
            
            const result = await response.json();
            setBonsLivraison(result.data || []);
            setCurrentPage(1); // Reset à la première page quand les données changent
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const fetchStats = async () => {
        try {
            const token = localStorage.getItem("token");
            const response = await fetch(
                'https://sawd.julesferry-ga.com/api/bons_livraison.php?action=stats',
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            
            if (response.ok) {
                const result = await response.json();
                setStats(result.data);
            }
        } catch (err) {
            console.error("Erreur stats", err);
        }
    };

    const fetchBonLivraisonDetails = async (id) => {
        try {
            const token = localStorage.getItem("token");
            const response = await fetch(
                `https://sawd.julesferry-ga.com/api/bons_livraison.php?id=${id}`,
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            
            if (!response.ok) throw new Error("Erreur chargement détails");
            
            const result = await response.json();
            return result.data;
        } catch (err) {
            console.error("Erreur:", err);
            showError("Impossible de charger les détails du bon de livraison");
            return null;
        }
    };

    const getStatutBadge = (statut) => {
        const configs = {
            brouillon: { color: 'secondary', icon: 'ri-draft-line', label: 'Brouillon' },
            prepare: { color: 'primary', icon: 'ri-stack-line', label: 'Préparé' },
            livre: { color: 'success', icon: 'ri-check-double-line', label: 'Livré' },
            annule: { color: 'danger', icon: 'ri-close-circle-line', label: 'Annulé' }
        };
        const config = configs[statut] || configs.brouillon;
        return (
            <Badge 
                bg={config.color} 
                className="px-3 py-2 statut-badge"
                style={{
                    background: `linear-gradient(135deg, var(--bs-${config.color}), var(--bs-${config.color}-dark))`,
                    border: 'none',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                }}
            >
                <i className={`${config.icon} me-1`}></i>
                {config.label}
            </Badge>
        );
    };

    const estEnRetard = (dateLivraison) => {
        return new Date(dateLivraison) < new Date();
    };

    // Filtrage et tri des données
    const filteredBons = useMemo(() => {
        return bonsLivraison
            .filter(b => 
                b.numero_bl?.toLowerCase().includes(filterText.toLowerCase()) ||
                b.client_nom?.toLowerCase().includes(filterText.toLowerCase()) ||
                b.code_client?.toLowerCase().includes(filterText.toLowerCase()) ||
                b.destinataire?.toLowerCase().includes(filterText.toLowerCase())
            )
            .sort((a, b) => {
                if (sortConfig.key === 'date_livraison') {
                    return sortConfig.direction === 'asc' 
                        ? new Date(a.date_livraison) - new Date(b.date_livraison)
                        : new Date(b.date_livraison) - new Date(a.date_livraison);
                }
                if (sortConfig.key === 'numero_bl') {
                    return sortConfig.direction === 'asc'
                        ? (a.numero_bl || '').localeCompare(b.numero_bl || '')
                        : (b.numero_bl || '').localeCompare(a.numero_bl || '');
                }
                return 0;
            });
    }, [bonsLivraison, filterText, sortConfig]);

    // Pagination
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentItems = filteredBons.slice(indexOfFirstItem, indexOfLastItem);
    const totalPages = Math.ceil(filteredBons.length / itemsPerPage);

    const handlePageChange = (pageNumber) => {
        setCurrentPage(pageNumber);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const renderPagination = () => {
        let items = [];
        const maxVisible = 5;
        let startPage = Math.max(1, currentPage - Math.floor(maxVisible / 2));
        let endPage = Math.min(totalPages, startPage + maxVisible - 1);

        if (endPage - startPage + 1 < maxVisible) {
            startPage = Math.max(1, endPage - maxVisible + 1);
        }

        // Première page
        if (startPage > 1) {
            items.push(
                <Pagination.Item key={1} onClick={() => handlePageChange(1)}>
                    1
                </Pagination.Item>
            );
            if (startPage > 2) {
                items.push(<Pagination.Ellipsis key="ellipsis-start" disabled />);
            }
        }

        // Pages centrales
        for (let number = startPage; number <= endPage; number++) {
            items.push(
                <Pagination.Item 
                    key={number} 
                    active={number === currentPage}
                    onClick={() => handlePageChange(number)}
                >
                    {number}
                </Pagination.Item>
            );
        }

        // Dernière page
        if (endPage < totalPages) {
            if (endPage < totalPages - 1) {
                items.push(<Pagination.Ellipsis key="ellipsis-end" disabled />);
            }
            items.push(
                <Pagination.Item key={totalPages} onClick={() => handlePageChange(totalPages)}>
                    {totalPages}
                </Pagination.Item>
            );
        }

        return items;
    };

    const handleSort = (key) => {
        setSortConfig(prev => ({
            key,
            direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
        }));
    };

    const SortableHeader = ({ field, children }) => (
        <th 
            onClick={() => handleSort(field)}
            style={{ cursor: 'pointer', userSelect: 'none' }}
            className="sortable-header"
        >
            <div className="d-flex align-items-center gap-1">
                {children}
                {sortConfig.key === field && (
                    <i className={`ri-arrow-${sortConfig.direction === 'asc' ? 'up' : 'down'}-s-line`}></i>
                )}
            </div>
        </th>
    );

    const showSuccess = (message) => {
        setSuccessMessage(message);
        setShowSuccessAlert(true);
        setTimeout(() => setShowSuccessAlert(false), 3000);
    };

    const showError = (message) => {
        setErrorMessage(message);
        setShowErrorAlert(true);
        setTimeout(() => setShowErrorAlert(false), 3000);
    };

    const handleMarquerLivre = async (id) => {
        openConfirmModal(
            id,
            "Confirmer la livraison",
            "Êtes-vous sûr de vouloir marquer ce bon de livraison comme <strong>livré</strong> ? Cette action mettra à jour le statut.",
            "success",
            async (confirmId) => {
                setIsProcessing(true);
                try {
                    const token = localStorage.getItem("token");
                    const response = await fetch(
                        'https://sawd.julesferry-ga.com/api/bons_livraison.php',
                        {
                            method: 'POST',
                            headers: { 
                                'Authorization': `Bearer ${token}`,
                                'Content-Type': 'application/json'
                            },
                            body: JSON.stringify({
                                action: 'marquer_livre',
                                id: confirmId
                            })
                        }
                    );

                    const result = await response.json();

                    if (result.success) {
                        setShowConfirmModal(false);
                        showSuccess("✅ Bon de livraison marqué comme livré");
                        fetchBonsLivraison();
                        fetchStats();
                    } else {
                        showError(result.message || "Erreur lors de la mise à jour");
                    }
                } catch (err) {
                    console.error("Erreur:", err);
                    showError("Erreur de connexion au serveur");
                } finally {
                    setIsProcessing(false);
                }
            }
        );
    };

    // Fonction d'impression avec le même format que les factures
    const imprimerBonLivraison = (bl) => {
        const printWindow = window.open('', '_blank');
        
        const company = {
            name: societeInfos?.nom || 'GESTION STOCK',
            address: societeInfos?.adresse || '123 Rue du Commerce, Libreville',
            phone: societeInfos?.telephone || '+241 01 23 45 67',
            email: societeInfos?.email || 'contact@gestionstock.ga',
            site_web: societeInfos?.site_web || 'www.gestionstock.ga',
            rc: societeInfos?.rc || 'LB/2025/00123',
            nif: societeInfos?.nif || '20251234567',
            patente: societeInfos?.patente || 'PAT0012345',
            logo_url: societeInfos?.logo_url 
        };

        const statut = bl?.statut === 'livre' ? 'LIVRÉ' : 
                      bl?.statut === 'prepare' ? 'PRÉPARÉ' : 
                      bl?.statut === 'annule' ? 'ANNULÉ' : 'BROUILLON';
        
        const statutColor = bl?.statut === 'livre' ? '#10b981' :
                           bl?.statut === 'prepare' ? '#3b82f6' :
                           bl?.statut === 'annule' ? '#ef4444' : '#6b7280';

        const logoText = company.name.substring(0, 2).toUpperCase();
        const logoSrc = company.logo_url || '';

        const totalProduits = bl?.lignes?.reduce((acc, l) => acc + (parseInt(l.quantite) || 0), 0) || 0;

        const printStyles = `
            * {
                margin: 0;
                padding: 0;
                box-sizing: border-box;
            }

            body {
                background: #f5f7fa;
                font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;
                padding: 30px;
                color: #2c3e50;
            }

            .document-paper {
                max-width: 900px;
                margin: 0 auto;
                background: white;
                box-shadow: 0 5px 20px rgba(0,0,0,0.1);
                padding: 30px 35px;
                border-radius: 8px;
            }

            /* En-tête */
            .document-header {
                display: flex;
                justify-content: space-between;
                align-items: flex-start;
                margin-bottom: 25px;
                padding-bottom: 20px;
                border-bottom: 2px solid ${statutColor};
            }

            .logo {
                width: 80px;
                height: 80px;
                object-fit: contain;
            }
            .logo-fallback {
                width: 80px;
                height: 80px;
                background: linear-gradient(135deg, ${statutColor}, ${statutColor}dd);
                color: white;
                display: flex;
                align-items: center;
                justify-content: center;
                font-weight: bold;
                font-size: 2rem;
                border-radius: 8px;
                box-shadow: 0 2px 10px ${statutColor}50;
            }

            .company-name {
                font-size: 1.8rem;
                font-weight: 700;
                color: #1e293b;
                margin: 0;
            }

            .company-website {
                font-size: 0.8rem;
                color: #64748b;
            }

            .document-badge {
                text-align: right;
            }

            .doc-type {
                font-size: 1.2rem;
                font-weight: 600;
                color: ${statutColor};
                margin-bottom: 5px;
            }

            .doc-number {
                font-size: 0.9rem;
                color: #5a6a7e;
                background: #f1f5f9;
                padding: 4px 12px;
                border-radius: 4px;
                display: inline-block;
            }

            .status-badge {
                display: inline-block;
                padding: 4px 12px;
                border-radius: 4px;
                font-size: 0.8rem;
                font-weight: 600;
                text-transform: uppercase;
                margin-top: 8px;
                background: ${statutColor};
                color: white;
            }

            /* Section client */
            .client-section {
                background: #f8fafc;
                padding: 20px;
                border-radius: 6px;
                margin: 20px 0;
                display: flex;
                justify-content: space-between;
                align-items: flex-start;
            }

            .client-info h3 {
                font-size: 0.8rem;
                font-weight: 600;
                color: ${statutColor};
                text-transform: uppercase;
                letter-spacing: 0.3px;
                margin-bottom: 12px;
            }

            .client-name {
                font-size: 1.2rem;
                font-weight: 600;
                color: #1e293b;
                margin-bottom: 8px;
            }

            .client-details {
                font-size: 0.85rem;
                color: #4a5a6e;
                line-height: 1.6;
            }

            .client-details span {
                display: block;
            }

            .document-dates {
                text-align: right;
            }

            .date-item {
                margin-bottom: 8px;
            }

            .date-item .label {
                font-size: 0.75rem;
                color: #6f7d95;
                display: block;
            }

            .date-item .value {
                font-size: 1rem;
                font-weight: 500;
                color: #1e293b;
            }

            /* Informations livraison */
            .delivery-info {
                background: #f8fafc;
                padding: 20px;
                border-radius: 6px;
                margin: 20px 0;
                border-left: 4px solid ${statutColor};
            }

            .delivery-title {
                font-size: 1rem;
                font-weight: 600;
                color: ${statutColor};
                margin-bottom: 15px;
                display: flex;
                align-items: center;
                gap: 8px;
            }

            .delivery-grid {
                display: grid;
                grid-template-columns: repeat(2, 1fr);
                gap: 15px;
            }

            .delivery-item {
                font-size: 0.85rem;
            }

            .delivery-item strong {
                color: #4b5563;
                display: block;
                margin-bottom: 4px;
                font-size: 0.75rem;
                text-transform: uppercase;
                letter-spacing: 0.5px;
            }

            /* Tableau */
            .table-container {
                margin: 25px 0;
                border: 1px solid #e2e8f0;
                border-radius: 6px;
                overflow: hidden;
            }

            table {
                width: 100%;
                border-collapse: collapse;
                font-size: 0.85rem;
            }

            th {
                background: #f1f5f9;
                color: #1e293b;
                font-weight: 600;
                padding: 12px 10px;
                text-align: left;
                border-bottom: 1px solid #cbd5e1;
            }

            td {
                padding: 10px;
                border-bottom: 1px solid #e2e8f0;
            }

            tr:last-child td {
                border-bottom: none;
            }

            .ref-cell {
                font-family: 'Courier New', monospace;
                font-size: 0.8rem;
                color: ${statutColor};
                background: ${statutColor}20;
                padding: 3px 8px;
                border-radius: 3px;
                display: inline-block;
            }

            .text-center {
                text-align: center;
            }

            .text-end {
                text-align: right;
            }

            /* Totaux */
            .totals-wrapper {
                display: flex;
                justify-content: flex-end;
                margin-top: 20px;
            }

            .totals-card {
                width: 300px;
            }

            .total-line {
                display: flex;
                justify-content: space-between;
                padding: 8px 0;
                border-bottom: 1px dashed #d1d9e6;
            }

            .total-line:last-child {
                border-bottom: none;
            }

            .total-line .label {
                color: #4a5a6e;
            }

            .total-line .value {
                font-weight: 500;
            }

            .total-line.highlight {
                background: ${statutColor}20;
                margin-top: 8px;
                padding: 12px;
                border-radius: 4px;
                font-weight: 600;
            }

            .total-line.highlight .label,
            .total-line.highlight .value {
                color: ${statutColor};
                font-size: 1.1rem;
            }

            /* Notes */
            .notes-section {
                background: #fef3c7;
                padding: 12px 15px;
                border-left: 3px solid #f59e0b;
                margin: 20px 0;
                font-size: 0.9rem;
                color: #92400e;
            }

            /* Signatures */
            .signatures {
                display: flex;
                justify-content: space-between;
                margin-top: 40px;
                padding: 0 20px;
            }

            .signature-box {
                width: 200px;
                text-align: center;
            }

            .signature-line {
                border-top: 1px dashed #000;
                padding-top: 5px;
                margin-top: 30px;
            }

            .signature-label {
                font-size: 0.7rem;
                color: #64748b;
            }

            /* Pied de page */
            .document-footer {
                margin-top: 35px;
                padding-top: 15px;
                border-top: 1px solid #d1d9e6;
                display: flex;
                justify-content: space-between;
                align-items: center;
                font-size: 0.75rem;
                color: #7f8c9f;
            }

            .legal-info span {
                margin-right: 20px;
            }

            @media print {
                body {
                    background: white;
                    padding: 0;
                }
                .document-paper {
                    box-shadow: none;
                    padding: 20px;
                }
                th {
                    background: #f1f5f9 !important;
                    -webkit-print-color-adjust: exact;
                    print-color-adjust: exact;
                }
            }
        `;

        const printContent = `
            <!DOCTYPE html>
            <html lang="fr">
            <head>
                <meta charset="UTF-8">
                <meta name="viewport" content="width=device-width, initial-scale=1.0">
                <title>Bon de livraison ${bl?.numero_bl || ''}</title>
                <style>
                    ${printStyles}
                </style>
            </head>
            <body>
                <div class="document-paper">
                    <!-- En-tête avec logo -->
                    <div class="document-header">
                        <div style="display: flex; align-items: center; gap: 15px;">
                            ${logoSrc ? `
                                <img src="${logoSrc}" alt="Logo" class="logo" onerror="this.style.display='none'; this.nextElementSibling.style.display='flex';">
                                <div class="logo-fallback" style="display: none;">${logoText}</div>
                            ` : `
                                <div class="logo-fallback">${logoText}</div>
                            `}
                            <div>
                                <div class="company-name">${company.name}</div>
                                <div class="company-website">${company.site_web}</div>
                            </div>
                        </div>
                        <div class="document-badge">
                            <div class="doc-type">BON DE LIVRAISON</div>
                            <div class="doc-number">N° ${bl?.numero_bl || ''}</div>
                            <div class="status-badge">${statut}</div>
                        </div>
                    </div>

                    <!-- Coordonnées société -->
                    <div style="font-size: 0.85rem; color: #5a6a7e; margin: 15px 0 20px 0; padding-bottom: 15px; border-bottom: 2px solid ${statutColor};">
                        <span style="margin-right: 20px;">📍 ${company.address}</span>
                        <span style="margin-right: 20px;">📞 ${company.phone}</span>
                        <span style="margin-right: 20px;">✉ ${company.email}</span>
                    </div>

                    <!-- Client -->
                    <div class="client-section">
                        <div class="client-info">
                            <h3>CLIENT</h3>
                            <div class="client-name">${bl?.client_nom || 'Client non spécifié'}</div>
                            <div class="client-details">
                                <span>Code: ${bl?.code_client || ''}</span>
                                ${bl?.client_telephone ? `<span>📍 ${bl.client_telephone}</span>` : ''}
                                ${bl?.client_email ? `<span>✉ ${bl.client_email}</span>` : ''}
                            </div>
                        </div>
                        <div class="document-dates">
                            <div class="date-item">
                                <span class="label">Date de livraison</span>
                                <span class="value">${formatDate(bl?.date_livraison)}</span>
                            </div>
                            ${bl?.numero_facture ? `
                                <div class="date-item">
                                    <span class="label">Facture associée</span>
                                    <span class="value">${bl.numero_facture}</span>
                                </div>
                            ` : ''}
                        </div>
                    </div>

                    <!-- Informations livraison -->
                    <div class="delivery-info">
                        <div class="delivery-title">
                            <i>📦</i> INFORMATIONS DE LIVRAISON
                        </div>
                        <div class="delivery-grid">
                            <div class="delivery-item">
                                <strong>Destinataire</strong>
                                ${bl?.destinataire || bl?.client_nom || 'Non spécifié'}
                            </div>
                            <div class="delivery-item">
                                <strong>Adresse</strong>
                                ${bl?.adresse_livraison || bl?.client_adresse || 'Non spécifiée'}
                            </div>
                        </div>
                    </div>

                    <!-- Notes -->
                    ${bl?.notes ? `
                        <div class="notes-section">
                            <strong>Note :</strong> ${bl.notes}
                        </div>
                    ` : ''}

                    <!-- Tableau des produits -->
                    <div class="table-container">
                        <table>
                            <thead>
                                <tr>
                                    <th>Réf.</th>
                                    <th>Désignation</th>
                                    <th class="text-center">Qté</th>
                                    <th>Lot</th>
                                    <th>Péremption</th>
                                </tr>
                            </thead>
                            <tbody>
                                ${bl?.lignes && bl.lignes.length > 0 
                                    ? bl.lignes.map(ligne => `
                                        <tr>
                                            <td><span class="ref-cell">${ligne.reference || ''}</span></td>
                                            <td>${ligne.designation || ''}</td>
                                            <td class="text-center fw-bold">${ligne.quantite || 0}</td>
                                            <td>${ligne.lot || '-'}</td>
                                            <td>${ligne.date_peremption ? formatDate(ligne.date_peremption) : '-'}</td>
                                        </tr>
                                    `).join('')
                                    : '<tr><td colspan="5" style="text-align: center; padding: 20px;">Aucun produit dans ce bon de livraison</td></tr>'
                                }
                            </tbody>
                        </table>
                    </div>

                    <!-- Totaux -->
                    <div class="totals-wrapper">
                        <div class="totals-card">
                            <div class="total-line">
                                <span class="label">Nombre de références</span>
                                <span class="value">${bl?.lignes?.length || 0}</span>
                            </div>
                            <div class="total-line highlight">
                                <span class="label">Quantité totale</span>
                                <span class="value">${totalProduits}</span>
                            </div>
                        </div>
                    </div>

                    <!-- Signatures -->
                    <div class="signatures">
                        <div class="signature-box">
                            <div class="signature-line"></div>
                            <div class="signature-label">Cachet et signature du transporteur</div>
                        </div>
                        <div class="signature-box">
                            <div class="signature-line"></div>
                            <div class="signature-label">Cachet et signature du client</div>
                        </div>
                    </div>

                    <!-- Pied de page -->
                    <div class="document-footer">
                        <div class="legal-info">
                            <span>RC: ${company.rc}</span>
                            <span>NIF: ${company.nif}</span>
                            <span>Patente: ${company.patente}</span>
                        </div>
                        <div>
                            Document généré le ${new Date().toLocaleDateString('fr-FR')}
                        </div>
                    </div>
                </div>
                
                <script>
                    window.onload = function() { 
                        setTimeout(function() {
                            window.print();
                        }, 500);
                    }
                </script>
            </body>
            </html>
        `;

        printWindow.document.write(printContent);
        printWindow.document.close();
    };

    const handlePrint = async (bl) => {
        try {
            // Si le BL n'a pas de lignes, on charge les détails complets
            let blData = bl;
            if (!bl.lignes || bl.lignes.length === 0) {
                const details = await fetchBonLivraisonDetails(bl.id_bon_livraison);
                if (details) {
                    blData = details;
                }
            }
            
            imprimerBonLivraison(blData);
        } catch (err) {
            console.error("Erreur impression:", err);
            showError("Impossible de générer le bon de livraison");
        }
    };

    const handlePreparer = async (id) => {
        openConfirmModal(
            id,
            "Confirmer la préparation",
            "Êtes-vous sûr de vouloir préparer ce bon de livraison ? Les produits seront déduits du stock.",
            "primary",
            async (confirmId) => {
                setIsProcessing(true);
                try {
                    const token = localStorage.getItem("token");
                    const userStr = localStorage.getItem("user");
                    const user = userStr ? JSON.parse(userStr) : null;
                    
                    // Récupérer les données actuelles du BL
                    const getResponse = await fetch(
                        `https://sawd.julesferry-ga.com/api/bons_livraison.php?id=${confirmId}`,
                        { headers: { 'Authorization': `Bearer ${token}` } }
                    );

                    if (!getResponse.ok) throw new Error("Erreur chargement du BL");
                    
                    const getResult = await getResponse.json();
                    const blData = getResult.data;
                    
                    // Mettre à jour le statut via l'API REST
                    const updateResponse = await fetch(
                        `https://sawd.julesferry-ga.com/api/bons_livraison.php?id=${confirmId}`,
                        {
                            method: 'PUT',
                            headers: { 
                                'Authorization': `Bearer ${token}`,
                                'Content-Type': 'application/json'
                            },
                            body: JSON.stringify({
                                ...blData,
                                statut: 'prepare',
                                id_utilisateur: user?.id || user?.id_utilisateur || 1
                            })
                        }
                    );

                    const updateResult = await updateResponse.json();

                    if (updateResult.success) {
                        setShowConfirmModal(false);
                        showSuccess("📦 Bon de livraison marqué comme préparé");
                        fetchBonsLivraison();
                        fetchStats();
                    } else {
                        showError(updateResult.message || "Erreur lors de la mise à jour");
                    }
                } catch (err) {
                    console.error("Erreur:", err);
                    showError("Erreur de connexion au serveur");
                } finally {
                    setIsProcessing(false);
                }
            }
        );
    };

    const handleAnnuler = async (id) => {
        openConfirmModal(
            id,
            "Confirmer l'annulation",
            "Êtes-vous sûr de vouloir annuler ce bon de livraison ? Cette action est irréversible.",
            "danger",
            async (confirmId) => {
                setIsProcessing(true);
                try {
                    const token = localStorage.getItem("token");
                    
                    const getResponse = await fetch(
                        `https://sawd.julesferry-ga.com/api/bons_livraison.php?id=${confirmId}`,
                        { headers: { 'Authorization': `Bearer ${token}` } }
                    );

                    if (!getResponse.ok) throw new Error("Erreur chargement du BL");
                    
                    const getResult = await getResponse.json();
                    const blData = getResult.data;
                    
                    const updateResponse = await fetch(
                        `https://sawd.julesferry-ga.com/api/bons_livraison.php?id=${confirmId}`,
                        {
                            method: 'PUT',
                            headers: { 
                                'Authorization': `Bearer ${token}`,
                                'Content-Type': 'application/json'
                            },
                            body: JSON.stringify({
                                ...blData,
                                statut: 'annule'
                            })
                        }
                    );

                    const updateResult = await updateResponse.json();

                    if (updateResult.success) {
                        setShowConfirmModal(false);
                        showSuccess("❌ Bon de livraison annulé");
                        fetchBonsLivraison();
                        fetchStats();
                    } else {
                        showError(updateResult.message || "Erreur lors de l'annulation");
                    }
                } catch (err) {
                    console.error("Erreur:", err);
                    showError("Erreur de connexion au serveur");
                } finally {
                    setIsProcessing(false);
                }
            }
        );
    };

    const ActionButton = ({ variant, icon, onClick, tooltip, disabled = false }) => (
        <OverlayTrigger
            placement="top"
            overlay={<Tooltip id={`tooltip-${variant}`}>{tooltip}</Tooltip>}
        >
            <Button
                variant={variant}
                size="sm"
                onClick={onClick}
                disabled={disabled}
                className="action-btn"
            >
                <i className={icon} style={{ fontSize: '1.1rem' }}></i>
            </Button>
        </OverlayTrigger>
    );

    if (loading) {
        return (
            <>
                <Header />
                <div className="main main-app p-3 p-lg-4">
                    <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '60vh' }}>
                        <div className="loading-container">
                            <Spinner animation="border" variant="primary" />
                            <p className="mt-3 text-muted">Chargement des bons de livraison...</p>
                        </div>
                    </div>
                </div>
            </>
        );
    }

    if (error) {
        return (
            <>
                <Header />
                <div className="main main-app p-3 p-lg-4">
                    <Alert variant="danger" className="text-center py-5">
                        <i className="ri-error-warning-line fs-48 mb-3 d-block"></i>
                        <h4>{error}</h4>
                        <Button 
                            variant="outline-danger" 
                            className="mt-3"
                            onClick={fetchBonsLivraison}
                        >
                            <i className="ri-refresh-line me-2"></i>
                            Réessayer
                        </Button>
                    </Alert>
                </div>
            </>
        );
    }

    return (
        <>
            <Header />
            <div className="main main-app p-3 p-lg-4">
                <div className="container-fluid">
                    {/* Alertes */}
                    {showSuccessAlert && (
                        <Alert variant="success" onClose={() => setShowSuccessAlert(false)} dismissible className="mb-4 alert-modern">
                            <i className="ri-checkbox-circle-line me-2 fs-5"></i>
                            {successMessage}
                        </Alert>
                    )}
                    
                    {showErrorAlert && (
                        <Alert variant="danger" onClose={() => setShowErrorAlert(false)} dismissible className="mb-4 alert-modern">
                            <i className="ri-error-warning-line me-2 fs-5"></i>
                            {errorMessage}
                        </Alert>
                    )}

                    {/* En-tête */}
                    <Row className="mb-4 fade-in">
                        <Col md={12}>
                            <div className="d-flex justify-content-between align-items-center header-section">
                                <div className="d-flex align-items-center gap-3">
                                    <div 
                                        className="page-icon-wrapper d-flex align-items-center justify-content-center" 
                                        style={{ 
                                            width: '70px', 
                                            height: '70px', 
                                            borderRadius: '20px',
                                            background: 'linear-gradient(135deg, rgba(67, 97, 238, 0.15), rgba(58, 86, 212, 0.15))',
                                            boxShadow: '0 10px 20px rgba(67, 97, 238, 0.15)',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            flexShrink: 0
                                        }}
                                    >
                                        <i className="ri-truck-line text-primary" style={{ fontSize: '36px', lineHeight: 1 }}></i>
                                    </div>
                                    <div>
                                        <h1 className="page-title mb-1" style={{ fontSize: '2rem', fontWeight: 700, color: '#1e293b' }}>
                                            Bons de livraison
                                        </h1>
                                        <p className="page-subtitle mb-0" style={{ fontSize: '1rem', color: '#64748b' }}>
                                            Gérez les bons de livraison et suivez les expéditions
                                        </p>
                                    </div>
                                </div>
                                <Button 
                                    variant="primary" 
                                    onClick={() => navigate('/ventes/bl/nouveau')}
                                    className="btn-gradient px-4 py-2 d-flex align-items-center gap-2"
                                    style={{
                                        background: 'linear-gradient(135deg, #4361ee, #3a56d4)',
                                        border: 'none',
                                        borderRadius: '12px',
                                        padding: '0.6rem 1.5rem',
                                        fontWeight: 500,
                                        boxShadow: '0 8px 16px rgba(67, 97, 238, 0.25)',
                                        transition: 'all 0.3s ease'
                                    }}
                                >
                                    <i className="ri-add-line" style={{ fontSize: '1.2rem' }}></i>
                                    <span>Nouveau BL</span>
                                </Button>
                            </div>
                        </Col>
                    </Row>

                    {/* Statistiques */}
                    {stats && (
                        <Row className="g-3 mb-4 stats-row">
                            <Col sm={6} lg={3}>
                                <Card className="stat-card border-0 shadow-sm h-100">
                                    <Card.Body className="p-3">
                                        <div className="d-flex align-items-center">
                                            <div 
                                                className="stat-icon-wrapper d-flex align-items-center justify-content-center me-3" 
                                                style={{ 
                                                    width: '64px', 
                                                    height: '64px', 
                                                    borderRadius: '18px',
                                                    background: 'linear-gradient(135deg, #4361ee, #3a56d4)',
                                                    boxShadow: '0 12px 24px rgba(67, 97, 238, 0.35)',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    flexShrink: 0
                                                }}
                                            >
                                                <i className="ri-truck-line text-white" style={{ fontSize: '32px', lineHeight: 1 }}></i>
                                            </div>
                                            <div className="stat-content" style={{ flex: 1, minWidth: 0 }}>
                                                <span className="stat-label d-block text-uppercase small fw-semibold text-secondary mb-1">
                                                    BL du mois
                                                </span>
                                                <div className="d-flex align-items-baseline gap-2 flex-wrap">
                                                    <h2 className="stat-value text-primary mb-0 fw-bold" style={{ fontSize: '2rem', lineHeight: 1.2 }}>
                                                        {stats.mois?.nb_bl || 0}
                                                    </h2>
                                                    <Badge 
                                                        bg="primary" 
                                                        className="px-2 py-1 rounded-pill d-inline-flex align-items-center gap-1"
                                                        style={{ 
                                                            background: 'rgba(67, 97, 238, 0.15)',
                                                            color: '#fff',
                                                            border: 'none',
                                                            fontSize: '0.75rem',
                                                            fontWeight: '600'
                                                        }}
                                                    >
                                                        <i className="ri-stack-line"></i>
                                                        {stats.mois?.nb_produits || 0}
                                                    </Badge>
                                                </div>
                                                <small className="stat-trend text-primary d-flex align-items-center gap-1 mt-1">
                                                    <i className="ri-arrow-up-line"></i>
                                                    <span className="text-truncate">produits ce mois</span>
                                                </small>
                                            </div>
                                        </div>
                                    </Card.Body>
                                </Card>
                            </Col>

                            <Col sm={6} lg={3}>
                                <Card className="stat-card border-0 shadow-sm h-100">
                                    <Card.Body className="p-3">
                                        <div className="d-flex align-items-center">
                                            <div 
                                                className="stat-icon-wrapper d-flex align-items-center justify-content-center me-3" 
                                                style={{ 
                                                    width: '64px', 
                                                    height: '64px', 
                                                    borderRadius: '18px',
                                                    background: 'linear-gradient(135deg, #f59e0b, #d97706)',
                                                    boxShadow: '0 12px 24px rgba(245, 158, 11, 0.35)',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    flexShrink: 0
                                                }}
                                            >
                                                <i className="ri-time-line text-white" style={{ fontSize: '32px', lineHeight: 1 }}></i>
                                            </div>
                                            <div className="stat-content" style={{ flex: 1, minWidth: 0 }}>
                                                <span className="stat-label d-block text-uppercase small fw-semibold text-secondary mb-1">
                                                    À livrer
                                                </span>
                                                <div className="d-flex align-items-baseline gap-2 flex-wrap">
                                                    <h2 className="stat-value text-warning mb-0 fw-bold" style={{ fontSize: '2rem', lineHeight: 1.2 }}>
                                                        {stats.statuts?.find(s => s.statut === 'prepare')?.nb || 0}
                                                    </h2>
                                                    <Badge 
                                                        bg="warning" 
                                                        className="px-2 py-1 rounded-pill d-inline-flex align-items-center gap-1"
                                                        style={{ 
                                                            background: 'rgba(245, 158, 11, 0.15)',
                                                            color: '#fff',
                                                            border: 'none',
                                                            fontSize: '0.75rem',
                                                            fontWeight: '600'
                                                        }}
                                                    >
                                                        <i className="ri-truck-line"></i>
                                                        En cours
                                                    </Badge>
                                                </div>
                                                <small className="stat-trend text-warning d-flex align-items-center gap-1 mt-1">
                                                    <i className="ri-truck-line"></i>
                                                    <span className="text-truncate">en préparation</span>
                                                </small>
                                            </div>
                                        </div>
                                    </Card.Body>
                                </Card>
                            </Col>

                            <Col sm={6} lg={3}>
                                <Card className="stat-card border-0 shadow-sm h-100">
                                    <Card.Body className="p-3">
                                        <div className="d-flex align-items-center">
                                            <div 
                                                className="stat-icon-wrapper d-flex align-items-center justify-content-center me-3" 
                                                style={{ 
                                                    width: '64px', 
                                                    height: '64px', 
                                                    borderRadius: '18px',
                                                    background: 'linear-gradient(135deg, #10b981, #059669)',
                                                    boxShadow: '0 12px 24px rgba(16, 185, 129, 0.35)',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    flexShrink: 0
                                                }}
                                            >
                                                <i className="ri-check-double-line text-white" style={{ fontSize: '32px', lineHeight: 1 }}></i>
                                            </div>
                                            <div className="stat-content" style={{ flex: 1, minWidth: 0 }}>
                                                <span className="stat-label d-block text-uppercase small fw-semibold text-secondary mb-1">
                                                    Livrés
                                                </span>
                                                <div className="d-flex align-items-baseline gap-2 flex-wrap">
                                                    <h2 className="stat-value text-success mb-0 fw-bold" style={{ fontSize: '2rem', lineHeight: 1.2 }}>
                                                        {stats.statuts?.find(s => s.statut === 'livre')?.nb || 0}
                                                    </h2>
                                                    <Badge 
                                                        bg="success" 
                                                        className="px-2 py-1 rounded-pill d-inline-flex align-items-center gap-1"
                                                        style={{ 
                                                            background: 'rgba(16, 185, 129, 0.15)',
                                                            color: '#fff',
                                                            border: 'none',
                                                            fontSize: '0.75rem',
                                                            fontWeight: '600'
                                                        }}
                                                    >
                                                        <i className="ri-check-line"></i>
                                                        Terminés
                                                    </Badge>
                                                </div>
                                                <small className="stat-trend text-success d-flex align-items-center gap-1 mt-1">
                                                    <i className="ri-check-line"></i>
                                                    <span className="text-truncate">livraisons effectuées</span>
                                                </small>
                                            </div>
                                        </div>
                                    </Card.Body>
                                </Card>
                            </Col>

                            <Col sm={6} lg={3}>
                                <Card className="stat-card border-0 shadow-sm h-100">
                                    <Card.Body className="p-3">
                                        <div className="d-flex align-items-center">
                                            <div 
                                                className="stat-icon-wrapper d-flex align-items-center justify-content-center me-3" 
                                                style={{ 
                                                    width: '64px', 
                                                    height: '64px', 
                                                    borderRadius: '18px',
                                                    background: 'linear-gradient(135deg, #3b82f6, #2563eb)',
                                                    boxShadow: '0 12px 24px rgba(59, 130, 246, 0.35)',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    flexShrink: 0
                                                }}
                                            >
                                                <i className="ri-calendar-line text-white" style={{ fontSize: '32px', lineHeight: 1 }}></i>
                                            </div>
                                            <div className="stat-content" style={{ flex: 1, minWidth: 0 }}>
                                                <span className="stat-label d-block text-uppercase small fw-semibold text-secondary mb-1">
                                                    Aujourd'hui
                                                </span>
                                                <div className="d-flex align-items-baseline gap-2 flex-wrap">
                                                    <h2 className="stat-value text-info mb-0 fw-bold" style={{ fontSize: '2rem', lineHeight: 1.2 }}>
                                                        {stats.aujourdhui?.nb || 0}
                                                    </h2>
                                                    <Badge 
                                                        bg="info" 
                                                        className="px-2 py-1 rounded-pill d-inline-flex align-items-center gap-1"
                                                        style={{ 
                                                            background: 'rgba(59, 130, 246, 0.15)',
                                                            color: '#fff',
                                                            border: 'none',
                                                            fontSize: '0.75rem',
                                                            fontWeight: '600'
                                                        }}
                                                    >
                                                        <i className="ri-truck-line"></i>
                                                        À livrer
                                                    </Badge>
                                                </div>
                                                <small className="stat-trend text-info d-flex align-items-center gap-1 mt-1">
                                                    <i className="ri-calendar-line"></i>
                                                    <span className="text-truncate">livraisons aujourd'hui</span>
                                                </small>
                                            </div>
                                        </div>
                                    </Card.Body>
                                </Card>
                            </Col>
                        </Row>
                    )}

                    {/* Filtres */}
                    <Card className="filter-card border-0 mb-4">
                        <Card.Body>
                            <Row className="g-3">
                                <Col md={4}>
                                    <InputGroup className="search-input">
                                        <InputGroup.Text>
                                            <i className="ri-search-line"></i>
                                        </InputGroup.Text>
                                        <Form.Control
                                            placeholder="Rechercher BL, client..."
                                            value={filterText}
                                            onChange={(e) => setFilterText(e.target.value)}
                                        />
                                        {filterText && (
                                            <Button 
                                                variant="outline-secondary" 
                                                onClick={() => setFilterText('')}
                                                className="border-0"
                                            >
                                                <i className="ri-close-line"></i>
                                            </Button>
                                        )}
                                    </InputGroup>
                                </Col>
                                <Col md={3}>
                                    <Form.Select 
                                        value={filtreStatut}
                                        onChange={(e) => setFiltreStatut(e.target.value)}
                                        className="filter-select"
                                    >
                                        <option value="tous">📋 Tous les statuts</option>
                                        <option value="brouillon">📝 Brouillons</option>
                                        <option value="prepare">📦 Préparés</option>
                                        <option value="livre">✅ Livrés</option>
                                        <option value="annule">❌ Annulés</option>
                                    </Form.Select>
                                </Col>
                                <Col md={5} className="d-flex justify-content-end align-items-center">
                                    <div className="filter-info">
                                        <span className="badge-count">{filteredBons.length}</span>
                                        <span className="text-muted ms-2">BL trouvé(s)</span>
                                    </div>
                                    <Button
                                        variant="link"
                                        className="text-muted ms-3"
                                        onClick={() => setShowFilters(!showFilters)}
                                    >
                                        <i className={`ri-equalizer-line fs-5 ${showFilters ? 'text-primary' : ''}`}></i>
                                    </Button>
                                </Col>
                            </Row>
                            
                            {showFilters && (
                                <Row className="mt-3">
                                    <Col md={3}>
                                        <Form.Select className="filter-select">
                                            <option>Trier par date</option>
                                            <option>Plus récents</option>
                                            <option>Plus anciens</option>
                                        </Form.Select>
                                    </Col>
                                    <Col md={3}>
                                        <Form.Select className="filter-select">
                                            <option>Filtrer par client</option>
                                            <option>Clients actifs</option>
                                            <option>Nouveaux clients</option>
                                        </Form.Select>
                                    </Col>
                                </Row>
                            )}
                        </Card.Body>
                    </Card>

                    {/* Liste des BL avec pagination */}
                    <Card className="table-card border-0">
                        <Card.Body className="p-0">
                            <div className="table-responsive">
                                <Table hover className="mb-0 align-middle custom-table">
                                    <thead>
                                        <tr>
                                            <SortableHeader field="numero_bl">N° BL</SortableHeader>
                                            <SortableHeader field="date_livraison">Date livraison</SortableHeader>
                                            <th>Client</th>
                                            <th>Destinataire</th>
                                            <th>Facture</th>
                                            <th>Statut</th>
                                            <th className="text-center">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {currentItems.length > 0 ? (
                                            currentItems.map((bl) => {
                                                const retard = estEnRetard(bl.date_livraison) && bl.statut !== 'livre' && bl.statut !== 'annule';
                                                
                                                return (
                                                    <tr key={bl.id_bon_livraison} className={`facture-row ${retard ? 'en-retard' : ''}`}>
                                                        <td>
                                                            <Link 
                                                                to={`/ventes/bl/${bl.id_bon_livraison}`} 
                                                                className="facture-link"
                                                                title={bl.numero_bl}
                                                            >
                                                                <span className="facture-num">
                                                                    {bl.numero_bl?.length > 8 
                                                                        ? bl.numero_bl.substring(0, 8) + '...' 
                                                                        : bl.numero_bl}
                                                                </span>
                                                            </Link>
                                                        </td>
                                                        <td>
                                                            <span className="facture-date">
                                                                {formatDate(bl.date_livraison)}
                                                                {retard && (
                                                                    <Badge bg="danger" className="ms-2 retard-badge">
                                                                        En retard
                                                                    </Badge>
                                                                )}
                                                            </span>
                                                        </td>
                                                        <td>
                                                            <div className="client-info">
                                                                <span className="client-name fw-bold">{bl.client_nom}</span>
                                                                <br></br><small className="client-code">{bl.code_client}</small>
                                                            </div>
                                                        </td>
                                                        <td>
                                                            <span>{bl.destinataire || '-'}</span>
                                                        </td>
                                                        <td>
                                                            {bl.numero_facture ? (
                                                                <Link to={`/ventes/factures/${bl.id_facture}`} className="text-primary">
                                                                    {bl.numero_facture}
                                                                </Link>
                                                            ) : (
                                                                <span className="text-muted">-</span>
                                                            )}
                                                        </td>
                                                        <td>{getStatutBadge(bl.statut)}</td>
                                                        <td className="text-center" style={{minWidth:'220px'}}>
                                                            <div className="actions-container d-flex justify-content-center gap-2">
                                                                <ActionButton
                                                                    variant="outline-info"
                                                                    icon="ri-eye-line"
                                                                    onClick={() => navigate(`/ventes/blDetails/${bl.id_bon_livraison}`)}
                                                                    tooltip="Voir détails"
                                                                />
                                                                {bl.statut === 'brouillon' && (
                                                                    <>
                                                                        <ActionButton
                                                                            variant="outline-primary"
                                                                            icon="ri-pencil-line"
                                                                            onClick={() => navigate(`/ventes/bl/modifier/${bl.id_bon_livraison}`)}
                                                                            tooltip="Modifier"
                                                                        />
                                                                        <ActionButton
                                                                            variant="outline-success"
                                                                            icon="ri-stack-line"
                                                                            onClick={() => handlePreparer(bl.id_bon_livraison)}
                                                                            tooltip="Préparer"
                                                                        />
                                                                    </>
                                                                )}
                                                                {bl.statut !== 'livre' && bl.statut !== 'annule' && (
                                                                    <ActionButton
                                                                        variant="outline-danger"
                                                                        icon="ri-close-circle-line"
                                                                        onClick={() => handleAnnuler(bl.id_bon_livraison)}
                                                                        tooltip="Annuler"
                                                                    />
                                                                )}
                                                                <ActionButton
                                                                    variant="outline-secondary"
                                                                    icon="ri-printer-line"
                                                                    onClick={() => handlePrint(bl)}
                                                                    tooltip="Imprimer"
                                                                />
                                                            </div>
                                                        </td>
                                                    </tr>
                                                );
                                            })
                                        ) : (
                                            <tr>
                                                <td colSpan="7" className="text-center py-5">
                                                    <div className="empty-state">
                                                        <i className="ri-truck-line empty-icon text-primary" style={{ fontSize: '4rem' }}></i>
                                                        <h5 className="mt-3">Aucun bon de livraison trouvé</h5>
                                                        <p className="text-muted mb-3">
                                                            {filterText 
                                                                ? "Aucun résultat ne correspond à votre recherche"
                                                                : "Commencez par créer votre premier bon de livraison"}
                                                        </p>
                                                        {filterText ? (
                                                            <Button 
                                                                variant="outline-primary"
                                                                onClick={() => setFilterText('')}
                                                            >
                                                                <i className="ri-refresh-line me-2"></i>
                                                                Réinitialiser
                                                            </Button>
                                                        ) : (
                                                            <Button 
                                                                variant="primary"
                                                                onClick={() => navigate('/ventes/bl/nouveau')}
                                                            >
                                                                <i className="ri-add-line me-2"></i>
                                                                Créer un BL
                                                            </Button>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </Table>
                            </div>

                            {/* Pagination améliorée */}
                            {totalPages > 1 && (
                                <div className="d-flex flex-column flex-md-row justify-content-between align-items-center p-3 border-top">
                                    <div className="text-muted small mb-2 mb-md-0">
                                        <i className="ri-file-list-line me-1"></i>
                                        Affichage de {indexOfFirstItem + 1} à {Math.min(indexOfLastItem, filteredBons.length)} sur {filteredBons.length} BL
                                    </div>
                                    <Pagination className="mb-0 flex-wrap justify-content-center">
                                        <Pagination.First 
                                            onClick={() => handlePageChange(1)}
                                            disabled={currentPage === 1}
                                        />
                                        <Pagination.Prev 
                                            onClick={() => handlePageChange(currentPage - 1)}
                                            disabled={currentPage === 1}
                                        />
                                        {renderPagination()}
                                        <Pagination.Next 
                                            onClick={() => handlePageChange(currentPage + 1)}
                                            disabled={currentPage === totalPages}
                                        />
                                        <Pagination.Last 
                                            onClick={() => handlePageChange(totalPages)}
                                            disabled={currentPage === totalPages}
                                        />
                                    </Pagination>
                                </div>
                            )}
                        </Card.Body>
                    </Card>
                </div>

                {/* MODAL DE CONFIRMATION */}
                <Modal 
                    show={showConfirmModal} 
                    onHide={() => setShowConfirmModal(false)} 
                    centered 
                    className="modern-modal"
                >
                    <Modal.Header closeButton className="border-0 pb-0">
                        <Modal.Title className="w-100">
                            <div className="text-center">
                                <div className="confirm-icon mb-3">
                                    {confirmVariant === 'success' && (
                                        <i className="ri-checkbox-circle-line text-success" style={{ fontSize: '4rem' }}></i>
                                    )}
                                    {confirmVariant === 'danger' && (
                                        <i className="ri-alert-line text-danger" style={{ fontSize: '4rem' }}></i>
                                    )}
                                    {confirmVariant === 'warning' && (
                                        <i className="ri-error-warning-line text-warning" style={{ fontSize: '4rem' }}></i>
                                    )}
                                    {confirmVariant === 'primary' && (
                                        <i className="ri-information-line text-primary" style={{ fontSize: '4rem' }}></i>
                                    )}
                                </div>
                                <h3 className="fw-bold">{confirmTitle}</h3>
                            </div>
                        </Modal.Title>
                    </Modal.Header>
                    <Modal.Body className="pt-2 text-center">
                        <p className="text-muted mb-4" dangerouslySetInnerHTML={{ __html: confirmMessage }}></p>
                        
                        {confirmVariant === 'success' && (
                            <Alert variant="info" className="text-start mb-4">
                                <div className="d-flex align-items-center">
                                    <i className="ri-information-line fs-4 me-3"></i>
                                    <div>
                                        <small className="d-block">✓ Marquer le bon comme livré</small>
                                        <small className="d-block">✓ Enregistrer la date de livraison</small>
                                    </div>
                                </div>
                            </Alert>
                        )}
                        
                        {confirmVariant === 'danger' && (
                            <Alert variant="warning" className="text-start mb-4">
                                <i className="ri-alert-line me-2"></i>
                                Cette action est irréversible.
                            </Alert>
                        )}
                    </Modal.Body>
                    <Modal.Footer className="border-0 pt-0 justify-content-center">
                        <Button 
                            variant="outline-secondary" 
                            onClick={() => setShowConfirmModal(false)}
                            className="px-4"
                            size="lg"
                            disabled={isProcessing}
                        >
                            <i className="ri-close-line me-2"></i>
                            Annuler
                        </Button>
                        <Button 
                            variant={confirmVariant} 
                            onClick={() => {
                                if (confirmAction && confirmId) {
                                    confirmAction(confirmId);
                                }
                            }} 
                            className="px-5"
                            size="lg"
                            disabled={isProcessing}
                        >
                            {isProcessing ? (
                                <>
                                    <Spinner size="sm" className="me-2" />
                                    Traitement...
                                </>
                            ) : (
                                <>
                                    <i className={`ri-${
                                        confirmVariant === 'success' ? 'check-double' :
                                        confirmVariant === 'danger' ? 'delete-bin' :
                                        confirmVariant === 'warning' ? 'alert' :
                                        'check'
                                    }-line me-2`}></i>
                                    Confirmer
                                </>
                            )}
                        </Button>
                    </Modal.Footer>
                </Modal>

                <Footer />
            </div>

            <style>{`
                /* Styles pour la pagination */
                .pagination {
                    gap: 5px;
                }
                
                .pagination .page-item .page-link {
                    border-radius: 8px;
                    border: 1px solid #e2e8f0;
                    color: #1e293b;
                    padding: 0.5rem 1rem;
                    transition: all 0.2s ease;
                    font-weight: 500;
                }
                
                .pagination .page-item.active .page-link {
                    background: linear-gradient(135deg, #4361ee, #3a56d4);
                    border-color: #4361ee;
                    color: white;
                    box-shadow: 0 4px 10px rgba(67, 97, 238, 0.3);
                }
                
                .pagination .page-item .page-link:hover {
                    background: #f1f5f9;
                    transform: translateY(-2px);
                    box-shadow: 0 4px 8px rgba(0,0,0,0.1);
                }
                
                .pagination .page-item.disabled .page-link {
                    opacity: 0.5;
                    pointer-events: none;
                }
                
                .facture-row.en-retard {
                    background: #fff5f5;
                }
                
                .retard-badge {
                    animation: pulse 2s infinite;
                }
                
                @keyframes pulse {
                    0% { transform: scale(1); }
                    50% { transform: scale(1.05); }
                    100% { transform: scale(1); }
                }
                
                /* Styles pour les boutons d'action */
                .actions-container {
                    display: flex;
                    justify-content: center;
                    align-items: center;
                    gap: 8px;
                    flex-wrap: wrap;
                }
                
                .action-btn {
                    width: 36px;
                    height: 36px;
                    padding: 0;
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    border-radius: 8px;
                    transition: all 0.2s ease;
                    border: 1px solid #e2e8f0;
                    background: white;
                    color: #64748b;
                }
                
                .action-btn:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 4px 8px rgba(0,0,0,0.1);
                }
                
                .action-btn.btn-outline-info:hover {
                    background: linear-gradient(135deg, #0891b2, #0e7490);
                    border-color: #0891b2;
                    color: white;
                }
                
                .action-btn.btn-outline-primary:hover {
                    background: linear-gradient(135deg, #4361ee, #3a56d4);
                    border-color: #4361ee;
                    color: white;
                }
                
                .action-btn.btn-outline-success:hover {
                    background: linear-gradient(135deg, #10b981, #059669);
                    border-color: #10b981;
                    color: white;
                }
                
                .action-btn.btn-outline-danger:hover {
                    background: linear-gradient(135deg, #ef4444, #dc2626);
                    border-color: #ef4444;
                    color: white;
                }
                
                .action-btn.btn-outline-secondary:hover {
                    background: linear-gradient(135deg, #64748b, #475569);
                    border-color: #64748b;
                    color: white;
                }
                
                .action-btn i {
                    font-size: 1.1rem;
                }
                
                /* Responsive */
                @media (max-width: 768px) {
                    .actions-container {
                        gap: 4px;
                    }
                    
                    .action-btn {
                        width: 32px;
                        height: 32px;
                    }
                    
                    .pagination .page-link {
                        padding: 0.4rem 0.8rem;
                        font-size: 0.9rem;
                    }
                }
            `}</style>   
        </>
    );
}